/**
 * DeleteApplicationModal Component
 *
 * Confirmation modal for deleting an application.
 * Requires two-factor authentication verification before deletion proceeds.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { useDeleteApplication } from '../hooks/useDeleteApplication';
import type { ApplicationSanitized } from '../../../types/application.types';
import type { TwoFactorRequiredResponse } from '../../../types/auth.types';
import clsx from 'clsx';

interface DeleteApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  application: ApplicationSanitized;
  onSuccess?: () => void;
}

/**
 * Format remaining seconds as "Xm Xs"
 */
function formatTime(seconds: number): string {
  if (seconds <= 0) return 'Expired';
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
}

/**
 * DeleteApplicationModal component
 */
export const DeleteApplicationModal: React.FC<DeleteApplicationModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  application,
  onSuccess,
}) => {
  const [confirmationCode, setConfirmationCode] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // 2FA state
  const [twoFactorState, setTwoFactorState] = useState<TwoFactorRequiredResponse | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [remainingTime, setRemainingTime] = useState(0);

  const { mutate: deleteApplication, isPending } = useDeleteApplication(tenantId, {
    onSuccess: () => {
      handleReset();
      onSuccess?.();
      onClose();
    },
    onError: (error) => {
      if (twoFactorState) {
        setLocalError(error);
      } else {
        setErrorMessage(error);
      }
    },
    onTwoFactorRequired: (response) => {
      setTwoFactorState(response);
      setRemainingTime(response.expiresIn);
      setErrorMessage('');
    },
  });

  // Reset all state
  const handleReset = useCallback(() => {
    setConfirmationCode('');
    setErrorMessage('');
    setTwoFactorState(null);
    setVerificationCode('');
    setLocalError(null);
    setRemainingTime(0);
  }, []);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      handleReset();
    }
  }, [isOpen, handleReset]);

  // Countdown timer for 2FA
  useEffect(() => {
    if (!twoFactorState || remainingTime <= 0) return;

    const interval = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [twoFactorState, remainingTime]);

  // Step 1: Initiate deletion (triggers 2FA)
  const handleDelete = () => {
    if (confirmationCode !== application.applicationCode) return;
    setErrorMessage('');
    deleteApplication({ applicationId: application.applicationId });
  };

  // Step 2: Submit 2FA verification code
  const handleVerify = useCallback(() => {
    if (!verificationCode || verificationCode.length !== 6) {
      setLocalError('Please enter a 6-digit verification code');
      return;
    }

    if (!/^\d{6}$/.test(verificationCode)) {
      setLocalError('Verification code must be 6 digits');
      return;
    }

    if (remainingTime <= 0) {
      setLocalError('Verification code has expired');
      return;
    }

    setLocalError(null);
    deleteApplication({
      applicationId: application.applicationId,
      verificationCodeId: twoFactorState!.codeId,
      verificationCode,
    });
  }, [verificationCode, remainingTime, twoFactorState, application.applicationId, deleteApplication]);

  const handleCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 6) {
      setVerificationCode(value);
      setLocalError(null);
    }
  }, []);

  const handleClose = () => {
    if (!isPending) {
      handleReset();
      onClose();
    }
  };

  const isCodeValid = confirmationCode === application.applicationCode;
  const isExpired = twoFactorState ? remainingTime <= 0 : false;
  const displayError = localError || (twoFactorState ? errorMessage : null);

  // 2FA Verification Phase
  if (twoFactorState) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} size="md">
        <form onSubmit={(e) => { e.preventDefault(); handleVerify(); }}>
          <Modal.Header>Two-Factor Authentication Required</Modal.Header>
          <Modal.Body>
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  To delete <span className="font-semibold">{application.applicationName}</span>, please
                  enter the verification code sent to <strong>{twoFactorState.destination}</strong>.
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="delete-app-verification-code" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Verification Code
                </label>
                <input
                  id="delete-app-verification-code"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  value={verificationCode}
                  onChange={handleCodeChange}
                  disabled={isPending || isExpired}
                  placeholder="000000"
                  autoComplete="off"
                  autoFocus
                  className={clsx(
                    'block w-full px-4 py-3 text-center text-2xl font-mono tracking-widest rounded-lg border shadow-sm',
                    'focus:outline-none focus:ring-2',
                    isExpired || displayError
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500',
                    'dark:bg-gray-700 dark:border-gray-600 dark:text-white',
                    (isPending || isExpired) && 'opacity-50 cursor-not-allowed'
                  )}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  Enter the 6-digit code
                </p>
              </div>

              {/* Timer and attempts info */}
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600 dark:text-gray-400">Expires in:</span>
                  <span
                    className={clsx(
                      'font-medium',
                      isExpired
                        ? 'text-red-600 dark:text-red-400'
                        : remainingTime <= 30
                        ? 'text-amber-600 dark:text-amber-400'
                        : 'text-gray-900 dark:text-gray-100'
                    )}
                  >
                    {formatTime(remainingTime)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-600 dark:text-gray-400">Attempts:</span>
                  <span
                    className={clsx(
                      'font-medium',
                      twoFactorState.attemptsRemaining <= 2
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-gray-900 dark:text-gray-100'
                    )}
                  >
                    {twoFactorState.attemptsRemaining} left
                  </span>
                </div>
              </div>

              {/* Error message */}
              {displayError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <p className="text-sm text-red-600 dark:text-red-400">{displayError}</p>
                </div>
              )}

              {/* Expired warning */}
              {isExpired && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    This verification code has expired. Please close this dialog and try again.
                  </p>
                </div>
              )}

              {/* Low attempts warning */}
              {twoFactorState.attemptsRemaining <= 2 && !isExpired && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    Warning: Only {twoFactorState.attemptsRemaining}{' '}
                    {twoFactorState.attemptsRemaining === 1 ? 'attempt' : 'attempts'} remaining before
                    this code is locked.
                  </p>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="flex items-center justify-end gap-3">
              <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="danger"
                loading={isPending}
                disabled={isExpired || verificationCode.length !== 6}
              >
                Verify & Delete
              </Button>
            </div>
          </Modal.Footer>
        </form>
      </Modal>
    );
  }

  // Confirmation Phase (initial)
  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>
        <span className="text-red-600">Delete Application</span>
      </Modal.Header>

      <Modal.Body>
        <div className="space-y-4">
          {/* Error Alert */}
          {errorMessage && (
            <Alert variant="error">
              {errorMessage}
            </Alert>
          )}

          {/* Warning */}
          <Alert variant="warning">
            <div className="space-y-2">
              <p className="font-semibold">This action cannot be undone!</p>
              <p className="text-sm">
                Deleting this application will permanently remove:
              </p>
              <ul className="text-sm list-disc list-inside ml-2">
                <li>The application configuration and metadata</li>
                <li>Associated roles and permissions</li>
                <li>Volume access grants</li>
                <li>Deployed Lambda function and API endpoints</li>
                <li>All deployment history</li>
              </ul>
              <p className="text-sm mt-2">
                A verification code will be sent to your email.
              </p>
            </div>
          </Alert>

          {/* Application Info */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div>
              <span className="text-sm font-medium text-gray-700">Application Name:</span>
              <p className="text-gray-900 font-semibold">{application.applicationName}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Application Code:</span>
              <p className="text-gray-900 font-mono bg-gray-200 px-2 py-1 rounded inline-block mt-1">
                {application.applicationCode}
              </p>
            </div>
          </div>

          {/* Confirmation Input */}
          <div>
            <Input
              label={
                <span>
                  Type <code className="bg-gray-200 px-1 rounded font-mono text-sm">{application.applicationCode}</code> to confirm
                </span>
              }
              placeholder="Enter application code"
              value={confirmationCode}
              onChange={(e) => setConfirmationCode(e.target.value)}
              disabled={isPending}
              autoComplete="off"
            />
            {confirmationCode && !isCodeValid && (
              <p className="mt-1 text-sm text-red-600">
                Application code does not match
              </p>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={!isCodeValid || isPending}
            loading={isPending}
          >
            Delete Application
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
